"use client";

import { useEffect, useRef, useState } from "react";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";
import type { AgentOrbTextureName } from "@ui/components/core/Orb/textures";
import { SwitcherButton } from "@ui/components/core/SwitcherButton";
import type { PillInactiveTextColor, PillSurface } from "@ui/components/core/pill-switcher-styles";
import type { Role } from "@elevenlabs/react";
import { AgentsHeroDemo, type AgentsHeroDemoHandle } from "./AgentsHeroDemo";
import { GetCallFlow } from "./GetCallFlow";
import { pxToRem } from "@ui/lib/utils";

const SWITCHER_MODE_IDS = ["chat", "voice"] as const;

type ModeId = (typeof SWITCHER_MODE_IDS)[number];
type VoiceTabMode = "demo" | "get_call";

export function AgentsOrbTabbedPanel({
  agentId,
  voiceId,
  initialMessages,
  className,
  inactiveTextColor = "default",
  switcherSurface,
  callAgentButtonText,
  getCallButtonText,
  defaultTab = "voice",
  onTabChange,
  chatAnchor = "top",
  orbTexture,
}: {
  agentId: string;
  voiceId?: string | null;
  initialMessages?: { role: Role; message: string }[];
  className?: string;
  inactiveTextColor?: PillInactiveTextColor;
  switcherSurface?: PillSurface;
  callAgentButtonText?: string | null;
  getCallButtonText?: string | null;
  defaultTab?: "chat" | "voice";
  onTabChange?: (mode: "chat" | "voice") => void;
  /** Where chat messages should anchor in chat mode. Defaults to "top". */
  chatAnchor?: "top" | "bottom";
  orbTexture: AgentOrbTextureName;
}) {
  const t = useTranslations("ClientKeys.hero_agent.switcher");
  const [selectedIndex, setSelectedIndex] = useState(defaultTab === "voice" ? 1 : 0);
  const [sessionActive, setSessionActive] = useState(false);
  const [voiceTabMode, setVoiceTabMode] = useState<VoiceTabMode>("demo");
  const demoRef = useRef<AgentsHeroDemoHandle | null>(null);

  const switcherOptions = SWITCHER_MODE_IDS.map(id => ({ id, label: t(id) }));
  const selectedMode: ModeId = SWITCHER_MODE_IDS[selectedIndex];
  const isVoice = selectedMode === "voice";

  useEffect(() => {
    onTabChange?.(isVoice ? "voice" : "chat");
  }, [isVoice, onTabChange]);

  const showGetCallFlow = isVoice && voiceTabMode === "get_call";

  return (
    <div
      className={clsx(
        "tw-flex tw-flex-col tw-items-center tw-gap-4 tw-w-full tw-h-full tw-min-h-0 tw-pt-6 tw-pb-2",
        className,
      )}
    >
      <SwitcherButton
        options={switcherOptions}
        selectedIndex={selectedIndex}
        onSelect={index => {
          if (sessionActive && index !== selectedIndex) {
            demoRef.current?.endSession();
          }
          setSelectedIndex(index);
          setVoiceTabMode("demo");
        }}
        size='sm'
        showRing={false}
        inactiveTextColor={isVoice ? "default" : inactiveTextColor}
        surface={isVoice ? "cream" : switcherSurface}
      />

      {showGetCallFlow ? (
        <div
          className='tw-bg-white tw-shadow-lg tw-max-w-full tw-overflow-y-auto tw-scrollbar-none tw-flex tw-flex-col tw-w-96'
          style={{
            borderRadius: "2rem",
            height: pxToRem(520),
          }}
        >
          <GetCallFlow agentId={agentId} onClose={() => setVoiceTabMode("demo")} className='tw-size-full' />
        </div>
      ) : (
        <AgentsHeroDemo
          ref={demoRef}
          agentId={agentId}
          voiceId={voiceId}
          mode={isVoice ? "voice" : "text"}
          initialMessages={initialMessages}
          onSessionActiveChange={setSessionActive}
          onModeChange={mode => setSelectedIndex(mode === "voice" ? 1 : 0)}
          callAgentButtonText={callAgentButtonText}
          getCallButtonText={getCallButtonText}
          onGetCallRequested={() => setVoiceTabMode("get_call")}
          chatAnchor={chatAnchor}
          orbTexture={orbTexture}
        />
      )}
    </div>
  );
}
